<template>
  <div>
    <div class="w-full px-6 h-20 border-b">
      <div
        class="max-w-7xl h-full w-full flex justify-center items-center mx-auto relative"
      >
        <h4 class="text-yank">Media Details</h4>
        <div
          v-if="!$page.props.user"
          class="absolute right-0 inset-y-0 hidden lg:flex items-center mr-10"
        >
          <AuthButtons />
        </div>
      </div>
    </div>
    <div
      class="p-6 lg:px-20 lg:py-12 space-y-8 lg:space-y-12 max-w-7xl w-full mx-auto"
    >
      <div
        class="flex flex-col lg:flex-row justify-between space-y-8 lg:space-y-0 lg:space-x-16"
      >
        <div class="w-full lg:w-6/12 flex-shrink-0">
          <!-- Show placeholder when no previews and no media files -->
          <div v-if="shouldShowPlaceholder" class="w-full">
            <div
              class="bg-smoke rounded-md flex items-center justify-center"
              style="height: 320px"
            >
              <img
                src="/images/picture.jpeg"
                alt="No media available"
                class="rounded-md w-auto h-full mx-auto object-cover"
              />
            </div>
          </div>

          <!-- Show carousel when there are previews or media files -->
          <template v-else>
            <flickity
              class="carousel-main"
              ref="flickity"
              :options="mainCarousel"
            >
              <template v-if="videoFile">
                <div
                  class="media-carousel-cell bg-smoke rounded-md overflow-hidden flex w-full items-center justify-center"
                  style="height: 320px"
                >
                  <div
                    class="flex-1"
                    style="
                      padding-top: 56.25%;
                      position: relative;
                      overflow: hidden;
                    "
                  >
                    <iframe
                      ref="videoIframe"
                      frameborder="0"
                      allowfullscreen=""
                      scrolling="no"
                      allow="autoplay;fullscreen"
                      :src="videoPlayerUrl(videoFile)"
                      style="
                        position: absolute;
                        height: 100%;
                        width: 100%;
                        left: 0px;
                        top: 0px;
                      "
                    ></iframe>
                  </div>
                </div>
              </template>
              <div
                v-for="(image, index) in media.previews"
                :key="`image-${index}`"
                class="relative media-carousel-cell bg-smoke rounded-md"
                style="height: 320px"
              >
                <div
                  v-if="!videoFile"
                  class="cursor-pointer absolute bg-black bg-opacity-70 rounded-lg top-3 right-2 items-center justify-center text-sm w-28 h-8"
                  @click="setEditorImage(image)"
                >
                  <span class="absolute top-2 left-3 text-white text-xs"
                    >Open Editor</span
                  >
                  <OpenEditor class="absolute top-2.5 right-3" />
                </div>
                <img
                  class="rounded-md w-auto h-full mx-auto object-cover cursor-pointer"
                  :src="image"
                  :alt="media.name"
                />
              </div>
            </flickity>
            <flickity
              v-if="shouldShowNavCarousel"
              class="carousel-nav mt-6 hidden md:block"
              ref="flickityNav"
              :options="navCarousel"
            >
              <template v-if="videoFile">
                <div
                  class="nav-images-carousel-cell relative bg-smoke rounded-md overflow-hidden"
                >
                  <img
                    v-if="media.previews.length"
                    class="rounded-md w-full object-cover cursor-pointer md:h-32 lg:h-28"
                    :src="media.previews[0]"
                    :alt="media.name"
                  />
                  <div
                    class="absolute inset-0 z-10 bg-smoke bg-opacity-50 flex items-center justify-center"
                  >
                    <svg
                      class="h-16 w-16 text-white opacity-70"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="1"
                        d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                      />
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="1"
                        d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                </div>
              </template>
              <div
                v-for="(image, index) in media.previews"
                :key="`image-${index}`"
                class="nav-images-carousel-cell bg-smoke rounded-md"
              >
                <img
                  class="rounded-md w-auto mx-auto object-cover cursor-pointer md:h-32 lg:h-28"
                  :src="image"
                  :alt="media.name"
                />
              </div>
            </flickity>
          </template>
        </div>
        <!--Info-->
        <div class="flex-grow">
          <div>
            <h1 class="media-title font-bold text-2xl">{{ media.name }}</h1>
            <h2 class="font-medium media-subtitle text-aquamarine">
              {{ media.subtitle }}
            </h2>
            <p
              class="media-description mt-3 text-yankMiddle prose prose-sm"
              v-html="media.description"
            ></p>
          </div>
          <div class="mt-8">
            <span class="font-semibold text-payneFire mb-2 block text-sm"
              >Attributes</span
            >
            <dl class="text-payneHot text-sm">
              <div class="py-2 sm:py-2 flex justify-between">
                <dt>Media ID</dt>
                <dd class="text-right">#{{ media.id }}</dd>
              </div>
              <div class="py-2 sm:py-2 flex justify-between">
                <dt>Files #</dt>
                <dd class="text-right">
                  {{ media.files.length }}
                </dd>
              </div>
              <div
                v-if="hasDimensions"
                class="py-2 sm:py-2 flex justify-between"
              >
                <dt>Size</dt>
                <dd class="text-right">
                  {{ biggestDimensions.width }} x {{ biggestDimensions.height }}
                </dd>
              </div>

              <div
                v-if="videoFile && videoFile.duration"
                class="py-2 sm:py-2 flex justify-between"
              >
                <dt>Duration</dt>
                <dd class="text-right">
                  {{ duration(videoFile.duration) }}
                </dd>
              </div>
            </dl>
          </div>
          <div class="relative mt-8 lg:mt-20 flex items-center">
            <div class="flex-1">
              <template v-if="!$page.props.user">
                <inertia-link
                  class="bnt-primary text-sm w-full h-14 flex items-center justify-center"
                  :href="$route('login.show')"
                >
                  Download
                </inertia-link>
              </template>

              <template v-else>
                <template v-if="media.files.length > 1">
                  <button
                    v-on-clickaway="away"
                    @click.prevent="downloadOptions = !downloadOptions"
                    class="bnt-primary text-sm w-full h-14"
                    type="button"
                    dusk="download-button"
                  >
                    Download
                  </button>
                  <transition @enter="enter">
                    <div
                      v-if="downloadOptions"
                      class="absolute left-0 right-0 bg-white p-3 space-y-3 rounded-lg shadow-dropdown z-20 max-h-96 overflow-y-scroll"
                    >
                      <a
                        v-for="file in media.files"
                        :key="`file-${file.uuid}`"
                        :href="
                          $route('download', { type: 'media', uuid: file.uuid })
                        "
                        class="download-option truncate block"
                      >
                        {{ fileType(file.mime_type) }}
                        <span class="text-yankMiddle" v-if="file.dimensions">
                          ({{
                            `${file.dimensions.width} x ${file.dimensions.height}`
                          }})</span
                        >
                      </a>
                      <div class="divider border-t border-smoke"></div>
                      <a
                        class="download-option truncate block"
                        :href="
                          $route('download', { type: 'item', uuid: media.uuid })
                        "
                      >
                        Download All Files
                      </a>
                    </div>
                  </transition>
                </template>

                <template v-else-if="media.files.length == 1">
                  <a
                    dusk="download-button"
                    class="bnt-primary flex items-center justify-center text-sm w-full h-14"
                    :href="
                      $route('download', {
                        type: 'media',
                        uuid: media.files[0].uuid,
                      })
                    "
                  >
                    Download
                  </a>
                </template>
              </template>
            </div>
            <div class="ml-4">
              <button
                v-if="!media.favorited"
                @click="toggleFavorite"
                type="button"
                class="btn p-0 flex items-center justify-center h-14 w-14 border-2 border-yank border-opacity-10 hover:border-opacity-20 text-yank text-opacity-10 hover:text-opacity-20 transition"
              >
                <svg
                  v-if="!processingFavorite"
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                  />
                </svg>
                <svg
                  v-if="processingFavorite"
                  class="animate-spin h-6 w-6"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  ></circle>
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              </button>

              <button
                v-if="media.favorited"
                @click="toggleFavorite"
                type="button"
                class="btn p-0 flex items-center justify-center h-14 w-14 bg-aquamarine text-white hover:bg-opacity-90 transition"
              >
                <svg
                  v-if="!processingFavorite"
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-6 w-6"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fill-rule="evenodd"
                    d="M3.172 5.172a4 4 0 015.656 0L10 6.343l1.172-1.171a4 4 0 115.656 5.656L10 17.657l-6.828-6.829a4 4 0 010-5.656z"
                    clip-rule="evenodd"
                  />
                </svg>
                <svg
                  v-if="processingFavorite"
                  class="animate-spin h-6 w-6"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  ></circle>
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              </button>
            </div>
          </div>

          <div
            v-if="$page.props.user"
            class="mt-4 flex items-center justify-end"
          >
            <report-issue
              :media="media"
              class="text-xs flex items-center font-semibold text-payneHot underline hover:text-opacity-90 focus:outline-none"
            >
              Report Issue
              <FlagIcon class="ml-1 h-3" />
            </report-issue>
          </div>
        </div>
      </div>
      <section v-if="media.tags && media.tags.length">
        <h4 class="font-semibold text-payneFire mb-5">Item Tags</h4>
        <div class="flex flex-wrap">
          <inertia-link
            :href="$route('search', { 'filter[tag]': tag.name })"
            v-for="tag in media.tags"
            :key="`tag-${tag.slug}`"
            class="bg-smoke px-5 py-2 text-sm rounded-full text-yankMiddle font-light mr-3 mb-3"
          >
            {{ tag.name }}
          </inertia-link>
        </div>
      </section>

      <section v-if="relatedItems && relatedItems.length">
        <h4 class="font-semibold text-payneFire mb-5">Related Media</h4>
        <div class="flex flex-wrap">
          <ItemComponent
            :item="item"
            v-for="item in relatedItems"
            :key="item.id"
          />
        </div>
      </section>

      <section v-if="media.collections && media.collections.length">
        <h4 class="font-semibold text-payneFire mb-5">
          Also included in these Collections
        </h4>
        <div class="items-grid">
          <CollectionComponent
            :collection="collection"
            v-for="collection in media.collections"
            :key="collection.uuid"
          />
        </div>
      </section>
    </div>
    <PhotoEditor
      v-if="editorImage !== ''"
      :license="editorLicense"
      :image-path="editorImage"
      @export="registerExport"
      @close="closePhotoEditor"
      :options="{ library: { enableUpload: true } }"
    />

    <transition name="fade">
      <div
        v-if="loading"
        class="bottom-0 mb-5 flex justify-center absolute h-20 w-5/6"
      >
        <LoadingModal
          loadingText="Uploading image to editor"
          :loadingBarSize="1500"
        />
      </div>
    </transition>
  </div>
</template>

<script>
import Flickity from "vue-flickity";
import anime from "animejs";
import { mixin as clickaway } from "vue-clickaway";
import dayjs from "dayjs";
import AppLayout from "@/layout/app-layout";
import AuthButtons from "@/components/shared/AuthButtons";
import CollectionComponent from "@/components/shared/CollectionComponent";
import ReportIssue from "@/components/shared/ReportIssue";
import FlagIcon from "@/components/svg/FlagIcon";
import OpenEditor from "@/components/svg/OpenEditor";
import ItemComponent from "@/components/home/ItemComponent";
import PhotoEditor from "@/components/shared/PhotoEditor.vue";
import LoadingModal from "@/components/scheduler/Common/LoadingModal";

import { imglyLicense } from "@/utils/imgly-license";

var utc = require("dayjs/plugin/utc");
dayjs.extend(utc);

export default {
  name: "MediaDetails",

  layout: AppLayout,

  mixins: [clickaway],

  components: {
    ReportIssue,
    CollectionComponent,
    Flickity,
    ItemComponent,
    AuthButtons,
    FlagIcon,
    OpenEditor,
    PhotoEditor,
    LoadingModal,
  },

  props: {
    media: Object,

    relatedItems: Array,

    flash: Object,

    posted: {
      type: Boolean,
      required: true,
    },
  },

  metaInfo() {
    return {
      title: this.media.name,
    };
  },

  data() {
    return {
      downloadOptions: false,
      mainCarousel: {
        initialIndex: 0,
        prevNextButtons: window.innerWidth < 768,
        pageDots: false,
        freeScroll: false,
        wrapAround: false,
        contain: true,
        cellAlign: "center",
        imagesLoaded: true,
      },
      navCarousel: {
        initialIndex: 0,
        prevNextButtons: true,
        pageDots: false,
        freeScroll: false,
        wrapAround: false,
        contain: true,
        cellAlign: "left",
        imagesLoaded: true,
      },
      processingFavorite: false,
      editorImage: "",
      editorLicense: imglyLicense,
      loading: false,
      isDataLoaded: false,
    };
  },

  computed: {
    createdAt() {
      return dayjs(this.media.created_at).format("MMM D, YYYY");
    },

    hasDimensions() {
      return this.biggestDimensions != null;
    },

    biggestDimensions() {
      // Add defensive checks to prevent undefined errors
      if (!this.media.files || this.media.files.length === 0) {
        return null;
      }
      
      const firstFile = this.media.files[0];
      return firstFile && firstFile.dimensions ? firstFile.dimensions : null;
    },

    videoFile() {
      if (!this.media.files || this.media.files.length === 0) {
        return null;
      }
      return this.media.files.find((file) =>
        file.mime_type && file.mime_type.startsWith("video/")
      );
    },

    shouldShowPlaceholder() {
      // Add defensive check for media.files and ensure data is loaded
      if (!this.isDataLoaded || !this.media || !this.media.files || this.media.files.length === 0) {
        return true;
      }

      // Check if there are no previews and no image/video files
      const hasNoPreviews =
        !this.media.previews || this.media.previews.length === 0;
      const hasNoMediaFiles = !this.media.files.some(
        (file) =>
          file.mime_type && (
            file.mime_type.startsWith("image/") ||
            file.mime_type.startsWith("video/")
          )
      );

      // Check if all files are PSD files
      const allFilesPSD = this.media.files.length > 0 && this.media.files.every(
        (file) => file.mime_type && file.mime_type.includes("photoshop")
      );

      return hasNoPreviews && (hasNoMediaFiles || allFilesPSD);
    },

    shouldShowNavCarousel() {
      // Show nav carousel only if there are multiple items to navigate
      const previewsCount = this.media.previews ? this.media.previews.length : 0;
      const hasVideo = !!this.videoFile;
      
      // Total items = video (if exists) + previews
      const totalItems = (hasVideo ? 1 : 0) + previewsCount;
      
      return totalItems > 1;
    },
  },

  watch: {
    flash: {
      handler(flash) {
        if (flash.success && flash.success !== "") {
          this.$toast.success(flash.success);
        }
        if (flash.error && flash.error !== "") {
          this.$toast.error(flash.error);
        }
      },
      deep: true,
    },
  },

  methods: {
    away() {
      this.downloadOptions = false;
    },

    enter(el) {
      anime({
        targets: el,
        translateY: 20,
        opacity: 1,
        duration: 800,
      });
    },

    updateSlides(event, pointer, cellElement, cellIndex) {
      this.$refs.flickity.select(cellIndex);
      this.$refs.flickityNav.select(cellIndex);
    },

    duration(seconds) {
      const time = dayjs.unix(seconds).utc();

      if (seconds > 3600) {
        return time.format("HH:mm:ss");
      }

      return time.format("mm:ss");
    },

    fileType(mime) {
      if (mime.includes("photoshop")) {
        return "Photoshop File";
      } else if (mime.startsWith("image/")) {
        let type = mime.split("/");
        return "Image " + type[1].toUpperCase();
      } else if (mime.startsWith("video/")) {
        let type = mime.split("/");
        return "Video " + type[1].toUpperCase();
      } else if (mime.includes("/zip")) {
        return "ZIP File";
      } else if (mime.includes("/pdf")) {
        return "PDF File";
      }

      return "File";
    },

    videoPlayerUrl(file) {
      let videoUrl = encodeURIComponent(file.url);
      let coverUrl = encodeURIComponent(this.media.previews[0]);

      return `https://onelineplayer.com/player.html?autoplay=false&autopause=false&muted=false&loop=false&url=${videoUrl}&poster=${coverUrl}&time=true&progressBar=true&overlay=true&muteButton=true&fullscreenButton=true&style=light&quality=1080p&playButton=true`;
    },

    pauseVideoPlayer(index) {
      if (index == 0) {
        return;
      }

      // Only pause/reset video if there's a video file and the iframe ref exists
      if (this.videoFile && this.$refs.videoIframe) {
        setTimeout(() => {
          this.$refs.videoIframe.src = "";
          this.$refs.videoIframe.src = this.videoPlayerUrl(this.videoFile);
        }, 350);
      }
    },

    toggleFavorite() {
      if (!this.$page.props.user) {
        this.$inertia.visit(this.$route("login.show"));
        return;
      }

      this.$inertia.patch(
        this.$route("media.favorites.toggle", this.media.id),
        {},
        {
          only: ["media"],
          preserveScroll: true,
          onStart: () => (this.processingFavorite = true),
          onFinish: () => (this.processingFavorite = false),
        }
      );
    },
    async setEditorImage(url) {
      this.loading = true;

      let fullResUrl = this.getFullResImageUrl(url);

      let response = await fetch(`/api/v1/media/to-string?url=${fullResUrl}`);
      let data = await response.json();

      this.editorImage = data.base64;
      this.loading = false;
    },

    closePhotoEditor() {
      this.editorImage = "";
    },

    registerExport() {
      this.$inertia.post(this.$route("media.export.store", this.media.id));
    },

    getFullResImageUrl(url) {
      const regex = /.com\/([0-9]*)\//m;
      const id = regex.exec(url)[1];

      let newUrl;

      this.media.files.forEach((file) => {
        if (file.url.includes(".com/" + id + "/")) {
          newUrl = file.url;
        }
      });

      return newUrl;
    },
  },

  mounted() {
    // Mark data as loaded when component is mounted
    this.isDataLoaded = true;
    
    // Only set up carousel event listeners if carousels exist (not showing placeholder)
    if (this.$refs.flickityNav && this.$refs.flickity) {
      this.$refs.flickityNav.on("staticClick", this.updateSlides);
      this.$refs.flickity.on("change", this.pauseVideoPlayer);

      // fix for flickity components overlapping when directly loading page
      setTimeout(() => {
        this.$refs.flickity.resize();
      }, 500);
    }
  },

  beforeDestroy() {
    // Only remove carousel event listeners if carousels exist
    if (this.$refs.flickityNav && this.$refs.flickity) {
      this.$refs.flickityNav.off("staticClick", this.updateSlides);
      this.$refs.flickity.off("change", this.pauseVideoPlayer);
    }
  },
};
</script>
